import { SkeletonText } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';

import { useCustomFieldValuesSearch } from 'src/core/common/components/CustomFieldFormField/CustomFieldAsyncAutocomplete/useCustomFieldValuesSearch';
import { QueryError } from 'src/core/common/components/QueryError';
import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useLoadCustomFieldValuesByIds } from 'src/core/modules/bookkeep/prepare-payables/components/PreparePayablesFiltersBar/components/PreparePayablesFiltersAdditionalFilters/hooks/useLoadCustomFieldValuesByIds';
import { useCustomFieldsQuery } from 'src/core/modules/budgets/apis';
import { shouldUseAsyncCustomFieldAutocomplete } from 'src/core/utils/custom-fields';

import { CommonSelect, type Option } from './CommonSelect';

export const CustomFieldSelect = ({
  selectedValues,
  onSelect,
  mode,
  customFieldId,
}: {
  selectedValues: { id: string; name: string }[] | undefined | null;
  onSelect: (values: { id: string; name: string }[] | null) => void;
  mode: 'create' | 'edit';
  customFieldId: string;
}) => {
  const customFieldsQueryState = useCustomFieldsQuery();
  const { mutateAsync: searchValues, data } =
    useCustomFieldValuesSearch(customFieldId);
  const loadValuesByIds = useLoadCustomFieldValuesByIds(customFieldId);
  const { t } = useTranslation('global');
  const [loadedValues, setLoadedValues] = useState<
    { id: string; name: string }[]
  >([]);

  const handleSearchAsync = (value: string) => {
    searchValues(value);
  };

  useEffect(() => {
    const loadValues = async () => {
      if (selectedValues?.length) {
        const valuesToLoad = selectedValues.filter(
          (value) => !loadedValues.some((loaded) => loaded.id === value.id),
        );

        if (valuesToLoad.length > 0) {
          const values = await loadValuesByIds(
            valuesToLoad.map((value) => value.id),
          );
          setLoadedValues((previous) => [...previous, ...values]);
        }
      }
    };

    loadValues();
  }, [selectedValues]);

  return (
    <QuerySuspense
      queryState={customFieldsQueryState}
      loading={<SkeletonText size="xxl" />}
      fallback={(error) => (
        <QueryError queryError={error} componentType="Callout" />
      )}
    >
      {(customFields) => {
        const currentCustomField = customFields.customFields.find(
          (customField) => customField.id === customFieldId,
        );
        if (!currentCustomField) {
          return null;
        }

        const shouldUseAsync =
          shouldUseAsyncCustomFieldAutocomplete(currentCustomField);

        const searchResults = data ?? [];

        const selectedOptions = selectedValues
          ? selectedValues
              .filter(
                (selectedKey) =>
                  !searchResults.some(
                    (option) => option.key === selectedKey.id,
                  ) &&
                  (loadedValues.some((value) => value.id === selectedKey.id) ||
                    currentCustomField.custom_fields_values.some(
                      (option) => option.id === selectedKey.id,
                    )),
              )
              .map((selectedKey) => {
                const loadedValue = loadedValues.find(
                  (value) => value.id === selectedKey.id,
                );
                if (loadedValue) {
                  return {
                    key: loadedValue.id,
                    label: loadedValue.name,
                  };
                }
                const staticOption =
                  currentCustomField.custom_fields_values.find(
                    (option) => option.id === selectedKey.id,
                  );
                if (staticOption) {
                  return {
                    key: staticOption.id,
                    label: staticOption.value,
                  };
                }
                const searchOption = searchResults.find(
                  (option) => option.key === selectedKey.id,
                );
                return {
                  key: selectedKey.id,
                  label: searchOption?.label ?? selectedKey.name,
                };
              })
          : [];

        const asyncOptions = [...searchResults, ...selectedOptions];

        const staticOptions =
          currentCustomField.custom_fields_values.map((option) => ({
            key: option.id,
            label: option.value,
          })) ?? [];

        const baseOptions: Option[] = shouldUseAsync
          ? asyncOptions
          : staticOptions;

        return (
          <CommonSelect
            selectedValues={selectedValues}
            valueFieldName={currentCustomField.name}
            onSelect={onSelect}
            isAsync={shouldUseAsync}
            onSearchAsync={handleSearchAsync}
            baseOptions={baseOptions}
            placeholder={
              shouldUseAsync
                ? t(
                    'approvalWorkflows.dimensions.customField.selectACustomFieldAsync',
                  )
                : t(
                    'approvalWorkflows.dimensions.customField.selectACustomField',
                  )
            }
            dimensionName="customField"
            mode={mode}
            customFieldId={customFieldId}
          />
        );
      }}
    </QuerySuspense>
  );
};
